// Componente CartSummary - Resumen del pedido en el carrito
// Muestra el subtotal, envío, total y botón para proceder al checkout

import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '../hooks/useCart'
import { useBusinessRulesContext } from '../hooks/useBusinessRulesContext'
import { formatCurrency } from '../utils/helpers'
import Button from './Button'
import './CartSummary.css'

/**
 * Componente CartSummary
 * Muestra el resumen del pedido con totales y botón de checkout
 */
function CartSummary() {
  const navigate = useNavigate()
  const { cartItems, getSubtotal, getShippingQuote, getFinalTotal, getCartItemCount } = useCart()
  const { rules } = useBusinessRulesContext()
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }

  // Por ahora, no tenemos la distancia del cliente, así que usamos null
  // En el futuro, esto se puede obtener del formulario de checkout
  const distance = null

  const subtotal = getSubtotal()
  const shippingQuote = getShippingQuote(distance)
  const total = getFinalTotal(distance)
  const itemCount = getCartItemCount()
  const hasInvalidStockItems = outOfStockPolicy.purchaseBehavior !== 'allow_purchase'
    ? cartItems.some(i => (i?.stock || 0) <= 0 || (i?.quantity || 0) > (i?.stock || 0))
    : false

  return (
    <div className="cart-summary">
      <h2 className="summary-title">Resumen del Pedido</h2>

      <div className="summary-details">
        {/* Subtotal */}
        <div className="summary-row">
          <span className="summary-label">Subtotal ({itemCount} {itemCount === 1 ? 'producto' : 'productos'})</span>
          <span className="summary-value">{formatCurrency(subtotal)}</span>
        </div>

        {/* Envío */}
        <div className="summary-row">
          <span className="summary-label">Envío</span>
          <span className="summary-value">
            {shippingQuote.type === 'negotiation' ? (
              <span className="free-shipping">A acordar</span>
            ) : shippingQuote.type === 'pending' ? (
              'Calculado en checkout'
            ) : shippingQuote.cost === 0 ? (
              <span className="free-shipping">¡Gratis!</span>
            ) : (
              formatCurrency(shippingQuote.cost)
            )}
          </span>
        </div>

        {shippingQuote.type === 'free' && (
          <p className="shipping-note">
            ¡Envío gratis aplicado!
          </p>
        )}

        {/* Divider */}
        <div className="summary-divider"></div>

        {/* Total */}
        <div className="summary-row summary-total">
          <span className="summary-label">Total</span>
          <span className="summary-value">{formatCurrency(total)}</span>
        </div>
      </div>

      {outOfStockPolicy.purchaseBehavior === 'inquiry_only' && hasInvalidStockItems ? (
        <div className="checkout-link">
          <Button
            variant="primary"
            size="large"
            className="checkout-button"
            onClick={() => navigate(outOfStockPolicy.inquiryChannel === 'help_page' ? '/ayuda' : '/ayuda#contacto')}
          >
            Consultar disponibilidad
          </Button>
        </div>
      ) : (
        <Link to="/checkout" className="checkout-link">
          <Button
            variant="primary"
            size="large"
            className="checkout-button"
            disabled={outOfStockPolicy.purchaseBehavior === 'block_purchase' && hasInvalidStockItems}
          >
            Proceder al Checkout
          </Button>
        </Link>
      )}

      <div className="summary-info">
        {outOfStockPolicy.purchaseBehavior === 'block_purchase' && hasInvalidStockItems ? (
          <p className="info-text">Hay productos sin stock en tu carrito. Ajustá cantidades para continuar.</p>
        ) : null}
        <p className="info-text">
          <span className="info-icon">🔒</span>
          Compra segura y protegida
        </p>
        <p className="info-text">
          <span className="info-icon">📦</span>
          Envío a todo el país
        </p>
        <p className="info-text">
          <span className="info-icon">↩️</span>
          Devoluciones fáciles
        </p>
      </div>
    </div>
  )
}

export default CartSummary

